import {
  AggregationBindingInfo,
  MetadataOptions,
  PropertyBindingInfo,
} from "sap/ui/base/ManagedObject";
import ComboBoxBase from "sap/m/ComboBoxBase";
import {
  FilterBinding,
  GetInfoFromBindingTemplate,
  GetValueForBindingInfo,
} from "./BindingFilterUtil";

/**
 * This file implements options filtering based on user input when the total number of options is very large.
 *
 * Explanation:
 * UI5 has an issue where aggregation bindings are limited to 100 items. This is a problem for akamont,
 * because we can have thousands of partners, and ComboBox cannot display all options.
 * One solution is to increase the limit as stated here: https://community.sap.com/t5/technology-q-a/sap-m-combobox-only-shows-100-items/qaq-p/475696.
 * But that's not a good solution, because the browser lags when ComboBox has 3000+ options.
 * Instead, we apply filtering to the model every time user changes the input value.
 * Another issue is that ComboBox doesn't have a "live change" event, so we have to override the "oninput" function that typescript doesn't know about.
 */

export const CustomComboBoxMetadata: MetadataOptions = {
  properties: {
    filterURL: { type: "string", defaultValue: "" },
  },
  events: {
    postControlFilter: {
      parameters: {
        oEvent: { type: "sap.ui.base.Event" },
        body: { type: "object" },
      },
    },
  },
};

export function CustomComboBoxUpdateItems(
  oComboBoxBase: ComboBoxBase,
  sReason: string
) {
  // this line is necessary to filter items if the "items" aggregation binding was updated after user input, e.g. on "selectionChange"
  if (sReason === "change") CustomComboBoxFilterOptions(oComboBoxBase);
}

export function CustomComboBoxFilterOptions(oComboBoxBase: ComboBoxBase) {
  const bindingInfo = oComboBoxBase.getBindingInfo(
    "items"
  ) as AggregationBindingInfo;

  const textBindingPath = bindingInfo.template?.getBindingInfo(
    "text"
  ) as PropertyBindingInfo;
  const additionalTextBindingPath = bindingInfo.template?.getBindingInfo(
    "additionalText"
  ) as PropertyBindingInfo;

  FilterBinding(oComboBoxBase.getValue(), oComboBoxBase.getBinding("items"), [
    getBindingInfoPath(textBindingPath),
    getBindingInfoPath(additionalTextBindingPath),
  ]);
}

function getBindingInfoPath(bindingInfo: PropertyBindingInfo): string {
  if (!bindingInfo) return "";
  const p = (
    bindingInfo.parts && bindingInfo.parts.length
      ? bindingInfo.parts[0]
      : bindingInfo
  ) as PropertyBindingInfo;

  const path = p.path || bindingInfo.path || "";
  return path;
}

export async function ApplyPostControlFilterAsync(
  oComboBoxBase: ComboBoxBase,
  searchValue?: string,
  selectedKeys?: string[],
  ignoreText = false
) {
  const bindingItemsInfo = oComboBoxBase.getBindingInfo(
    "items"
  ) as AggregationBindingInfo;

  const keyProp = GetInfoFromBindingTemplate(bindingItemsInfo, "key");
  const textProp = GetInfoFromBindingTemplate(bindingItemsInfo, "text");
  const additionalTextProp = GetInfoFromBindingTemplate(
    bindingItemsInfo,
    "additionalText"
  );

  const filterURL = oComboBoxBase.getProperty("filterURL");
  if (filterURL && filterURL !== "") {
    await firePostControlFilterAsync(oComboBoxBase, {
      value: ignoreText ? "" : searchValue,
      selectedKeys: selectedKeys ?? [],

      filterURL,
      key: GetValueForBindingInfo(keyProp),
      text: GetValueForBindingInfo(textProp),
      additionalText: GetValueForBindingInfo(additionalTextProp),
    });
  }
}

function firePostControlFilterAsync(oComboBoxBase: ComboBoxBase, params: any) {
  return new Promise((resolve, reject) => {
    // @ts-expect-error This event is added in metadata
    oComboBoxBase.firePostControlFilter({ ...params, resolve, reject });
  });
}

export function HasAggregationAllKeysInItems(
  oComboBoxBase: ComboBoxBase,
  keys: Set<string>
) {
  if (!keys.size) return true;
  const items = oComboBoxBase.getItems?.() ?? [];
  if (!items.length) return false;
  const present = new Set(
    items.map((i: any) => i.getKey?.() ?? i.getProperty?.("key"))
  );
  for (const k of keys) if (!present.has(k)) return false;
  return true;
}
